import { Controller } from '@hotwired/stimulus'

// Shared base for the hidden-id typeahead comboboxes. As you type, it fetches matching
// workspace records from a JSON suggestions endpoint (urlValue) and offers them in an
// ARIA combobox listbox; picking one writes the record's UUID into a hidden field (the
// value the form submits) and its label into the visible input, then dispatches a
// bubbling `change`. The endpoint is workspace-scoped and authorized server-side — this
// controller only renders what it returns and never decides which records are visible.
//
// Subclasses register under their OWN identifier (so the data-attribute prefix and the
// hidden field differ per entity) and MUST implement optionMarkup(item, index); they
// MAY override select() for post-select behaviour (e.g. reload a Turbo Frame).
//
// Current consumers (both extend this base): company-autocomplete and contact-combobox
// (#295). The app already had a ~130-line hidden-id typeahead (company-autocomplete);
// adding the #295 picker would have duplicated its keyboard-nav / stale-guard / escape
// logic, so those mechanics were extracted here — a fix now lands once for both.
// contact-autocomplete (the compose To-field email-filler — no hidden id, no frame
// reload) is a DIFFERENT behaviour and remains a tolerated, not-yet-migrated candidate;
// the `hasHiddenIdTarget` guard in select() keeps the base ready for such a
// hidden-id-less consumer, so migrating it later needs only an optionMarkup + select
// override.
export default class extends Controller {
  static targets = ['input', 'results', 'hiddenId']
  static values = { url: String, minLength: { type: Number, default: 1 } }

  connect() {
    this.activeIndex = -1
    this.suggestions = []
    // Monotonic request sequence — drop out-of-order responses (a slow-then-fast
    // keystroke could otherwise render stale suggestions). Same guard as
    // segment_preview_controller; no AbortController precedent in this codebase.
    this.seq = 0
    this.onDocumentClick = (event) => {
      if (!this.element.contains(event.target)) this.close()
    }
    document.addEventListener('click', this.onDocumentClick)
  }

  disconnect() {
    document.removeEventListener('click', this.onDocumentClick)
    clearTimeout(this.debounce)
  }

  // Debounced fetch on every keystroke. Editing the label invalidates any
  // previously-picked id until a fresh selection is made.
  search() {
    clearTimeout(this.debounce)
    if (this.hasHiddenIdTarget) this.hiddenIdTarget.value = ''
    const query = this.inputTarget.value.trim()
    if (query.length < this.minLengthValue) return this.close()

    this.debounce = setTimeout(() => this.fetchSuggestions(query), 200)
  }

  async fetchSuggestions(query) {
    const seq = ++this.seq
    const url = `${this.urlValue}?q=${encodeURIComponent(query)}`
    let matches
    try {
      const response = await fetch(url, { headers: { Accept: 'application/json' } })
      if (!response.ok) return this.close()
      matches = await response.json()
    } catch {
      return this.close()
    }
    if (seq !== this.seq) return // a newer keystroke already fired — ignore this stale response
    this.suggestions = matches
    this.render()
  }

  render() {
    if (this.suggestions.length === 0) return this.close()

    this.resultsTarget.innerHTML = this.suggestions
      .map((item, index) => this.optionMarkup(item, index))
      .join('')
    this.resultsTarget.hidden = false
    this.inputTarget.setAttribute('aria-expanded', 'true')
    this.activeIndex = -1
    this.inputTarget.removeAttribute('aria-activedescendant')
  }

  // Subclass hook: the listbox <li> markup for one record. MUST escape record-supplied
  // content via this.escape (the values are user-controlled — stored-XSS surface).
  optionMarkup(_item, _index) {
    throw new Error('optionMarkup(item, index) must be implemented by the subclass')
  }

  // Keyboard navigation over the open listbox.
  navigate(event) {
    if (this.resultsTarget.hidden) return

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        this.moveActive(1)
        break
      case 'ArrowUp':
        event.preventDefault()
        this.moveActive(-1)
        break
      case 'Enter':
        // The listbox is open (navigate returns early when hidden), so Enter commits a
        // pick: the highlighted option, or the top match when nothing is highlighted —
        // never letting the keystroke fall through and submit the enclosing form.
        event.preventDefault()
        this.select(this.suggestions[this.activeIndex >= 0 ? this.activeIndex : 0])
        break
      case 'Escape':
        this.close()
        break
    }
  }

  moveActive(step) {
    const options = Array.from(this.resultsTarget.children)
    if (options.length === 0) return

    this.activeIndex = (this.activeIndex + step + options.length) % options.length
    options.forEach((option, index) => {
      const active = index === this.activeIndex
      option.classList.toggle('active', active)
      option.setAttribute('aria-selected', active ? 'true' : 'false')
    })
    this.inputTarget.setAttribute('aria-activedescendant', options[this.activeIndex].id)
  }

  // mousedown (not click) so selection lands before the input's blur closes us.
  choose(event) {
    event.preventDefault()
    const index = Number(event.currentTarget.dataset.index)
    this.select(this.suggestions[index])
  }

  select(item) {
    if (!item) return
    if (this.hasHiddenIdTarget) {
      this.hiddenIdTarget.value = item.id
      // Bubble a change event so a dependent controller (e.g. segment-preview) can
      // recompute off the newly-picked id. Harmless where nothing listens.
      this.hiddenIdTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }
    this.inputTarget.value = item.name
    this.close()
    this.inputTarget.focus()
  }

  close() {
    this.resultsTarget.hidden = true
    this.resultsTarget.innerHTML = ''
    this.activeIndex = -1
    this.inputTarget.setAttribute('aria-expanded', 'false')
    this.inputTarget.removeAttribute('aria-activedescendant')
  }

  escape(value) {
    const span = document.createElement('span')
    span.textContent = value ?? ''
    return span.innerHTML
  }
}
